import { CheckCircle2, Circle, MapPin } from "lucide-react";

const inr = (n) => `₹${Number(n || 0).toLocaleString("en-IN")}`;

export function RateCard({ card }) {
  return (
    <div className="mt-2 w-full overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm" data-testid="ai-rate-card">
      <div className="bg-navy px-3 py-2 text-xs font-semibold text-white">
        {card.country} · {card.weight} kg chargeable
      </div>
      <div className="divide-y divide-slate-100">
        {(card.options || []).map((o, i) => (
          <div key={i} className="flex items-center justify-between px-3 py-2" data-testid={`ai-rate-option-${i}`}>
            <div className="min-w-0">
              <p className="truncate text-sm font-semibold text-navy">{o.courier}</p>
              <p className="text-xs text-slate-500">{o.service}{o.transit ? ` · ${o.transit}` : ""}</p>
            </div>
            <p className="ml-2 shrink-0 text-sm font-bold text-saffron">{inr(o.price)}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

export function TrackingCard({ card }) {
  const timeline = card.timeline || [];
  return (
    <div className="mt-2 w-full overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm" data-testid="ai-tracking-card">
      <div className="bg-navy px-3 py-2 text-xs text-white">
        <span className="font-mono font-semibold">{card.awb}</span>
        {card.courier ? <span className="ml-2 text-slate-300">{card.courier}</span> : null}
      </div>
      <div className="px-3 py-2">
        <p className="text-sm font-semibold text-navy">{card.status || "Status unavailable"}</p>
        {card.location ? (
          <p className="mt-0.5 flex items-center gap-1 text-xs text-slate-500"><MapPin className="h-3 w-3" />{card.location}</p>
        ) : null}
        {card.expected ? <p className="mt-0.5 text-xs text-slate-500">Expected: {card.expected}</p> : null}
        <div className="mt-2 space-y-1.5 border-l-2 border-slate-100 pl-3">
          {timeline.length === 0 && <p className="text-xs text-slate-400">No scans yet.</p>}
          {timeline.map((t, i) => (
            <div key={i} className="flex items-start gap-1.5" data-testid={`ai-track-event-${i}`}>
              {i === 0
                ? <Circle className="mt-0.5 h-3 w-3 shrink-0 fill-saffron text-saffron" />
                : <CheckCircle2 className="mt-0.5 h-3 w-3 shrink-0 text-emerald-500" />}
              <div className="min-w-0">
                <p className="text-xs font-medium text-navy">{t.status}</p>
                <p className="text-[11px] text-slate-400">{[t.location, t.timestamp && t.timestamp.slice(0, 16).replace("T", " ")].filter(Boolean).join(" · ")}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export function PickupCard({ card }) {
  return (
    <div className="mt-2 w-full rounded-xl border border-emerald-200 bg-emerald-50 px-3 py-2" data-testid="ai-pickup-card">
      <p className="text-xs text-emerald-700">Pickup Request ID</p>
      <p className="font-mono text-sm font-bold text-emerald-800">{card.request_id}</p>
      <p className="mt-1 text-xs text-slate-600">{card.name} · {card.city} · {card.date} ({card.time_slot})</p>
      <p className="text-xs text-slate-600">To: {card.country} · ~{card.weight} kg</p>
    </div>
  );
}

export function TicketCard({ card }) {
  return (
    <div className="mt-2 w-full rounded-xl border border-blue-200 bg-blue-50 px-3 py-2" data-testid="ai-ticket-card">
      <p className="text-xs text-blue-700">Support Ticket ID</p>
      <p className="font-mono text-sm font-bold text-blue-800">{card.ticket_id}</p>
      <p className="mt-1 text-xs text-slate-600">{card.category}{card.awb && card.awb !== "-" ? ` · AWB ${card.awb}` : ""}</p>
    </div>
  );
}

export function InfoCard({ card }) {
  return (
    <div className="mt-2 w-full rounded-xl border border-amber-200 bg-amber-50 px-3 py-2" data-testid="ai-info-card">
      {card.title ? <p className="text-sm font-semibold text-navy">{card.title}</p> : null}
      <ul className="mt-1 list-disc space-y-1 pl-4">
        {(card.lines || []).map((l, i) => <li key={i} className="text-xs text-slate-700">{l}</li>)}
      </ul>
    </div>
  );
}

export default function AiCards({ cards }) {
  if (!cards || !cards.length) return null;
  return (
    <>
      {cards.map((c, i) => {
        if (c.type === "rates") return <RateCard key={i} card={c} />;
        if (c.type === "tracking") return <TrackingCard key={i} card={c} />;
        if (c.type === "pickup") return <PickupCard key={i} card={c} />;
        if (c.type === "ticket") return <TicketCard key={i} card={c} />;
        return <InfoCard key={i} card={c} />;
      })}
    </>
  );
}
